import React, { useState, useEffect, useRef } from 'react';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Switch, Typography } from '@mui/material';
import FormSection from './FormSection.jsx';
import CurrencyInput from './CurrencyInput.jsx';

import DynamicAttributeRenderer from './DynamicAttributeRenderer.jsx';
import DynamicFeeRenderer from './DynamicFeeRenderer.jsx';
import { getAttributesByTypeName } from '../../utils/attributes';

const FeesStep = ({ formData = {}, updateFormData, merchantData = {} }) => {
  // Fee Category Template
  const [useFeeTemplate, setUseFeeTemplate] = useState(false);
  const [feeCategory, setFeeCategory] = useState(formData.fees?.fee_category || 'Pass Through');

  // Helper function to get attributes by type name from merchant data (same logic as getAttributesByTypeName)
  const getAttributesByTypeNameFromMerchantData = (typeName) => {
    // Check both possible structures: merchantData.data.attribute_types or merchantData.attribute_types
    const merchantAttributeTypes = merchantData?.data?.attribute_types || merchantData?.attribute_types;
    
    if (!merchantData || !merchantAttributeTypes || !Array.isArray(merchantAttributeTypes)) {
   
      return [];
    }
    
    const attributeType = merchantAttributeTypes.find(type =>
      type.name && type.name.toLowerCase() === typeName.toLowerCase()
    );
    
   
    
    // Transform attributes to use UUID as ID in edit mode
    const attributes = attributeType ? attributeType.attributes || [] : [];
    return attributes.map(attr => ({
      ...attr,
      id: attr.id  // Use UUID as the ID for FeesStep component
    }));
  };

  // Dynamic Attributes State
  const [dynamicAttributes, setDynamicAttributes] = useState({});
  const [attributeValues, setAttributeValues] = useState({});
  const [attributeEnabledValues, setAttributeEnabledValues] = useState({});
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);

  // Flat Rate Fields (for Flat Rate category)
  const [flatRate, setFlatRate] = useState('0.0000');
  const [flatRateAmex, setFlatRateAmex] = useState('0.0000');

  const isInitialized = useRef(false);

  // Load dynamic attributes on component mount or from merchantData (edit mode)
  useEffect(() => {
    const loadDynamicAttributes = async () => {
      setIsLoadingAttributes(true);
      try {
      
        const feeAttributeTypes = [
          'Visa/MasterCard, Discover',
          'American Express',
          'Pin Debit',
          'Authorization & Per Item Fees',
          'Monthly Fees',
          'Miscellaneous Fees',
          'Retail, Non-Retail, Wireless',
          'Wireless Fees'
        ];

        const attributeData = {};
        
        // Check if we have merchantData (edit mode) - use merchant data instead of API
        const merchantAttributeTypes = merchantData?.data?.attribute_types || merchantData?.attribute_types;
        if (merchantData && merchantAttributeTypes && Array.isArray(merchantAttributeTypes)) {
          
          for (const typeName of feeAttributeTypes) {
            const attributes = getAttributesByTypeNameFromMerchantData(typeName);
            if (attributes && attributes.length > 0) {
              attributeData[typeName] = attributes;
             
            }
          }
          
          setDynamicAttributes(attributeData);
          
          // Initialize attributeValues and attributeEnabledValues from formData
          if (formData.dynamic_attributes) {
            // Create mapping from numeric ID to UUID
            const idToUuidMap = {};
            Object.values(attributeData).flat().forEach(attr => {
              // attr.id is now the UUID, but we need to find the original numeric ID
              // The original numeric ID should be in the original attribute data
              const originalAttr = merchantAttributeTypes
                .flatMap(type => type.attributes || [])
                .find(origAttr => origAttr.uuid === attr.id);
              if (originalAttr) {
                idToUuidMap[originalAttr.id] = attr.id; // Map numeric ID to UUID
              }
            });
            
            // Map formData values from numeric ID keys to UUID keys
            const mappedAttributeValues = {};
            const enabledValues = {};
            
            Object.entries(formData.dynamic_attributes).forEach(([numericId, valueOrObject]) => {
              const uuid = idToUuidMap[numericId];
              if (uuid) {
                mappedAttributeValues[uuid] = valueOrObject;
                
                // Handle both old format (string value) and new format (object with value and uuid)
                const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
                // Enable if value exists and is not empty/default
                enabledValues[uuid] = value && value !== '0.0000' && value !== '0' && value !== '';
              }
            });
            
            setAttributeValues(mappedAttributeValues);
            setAttributeEnabledValues(enabledValues);
            
          
          }
        } else {
          // Create mode - use API (same as original logic)
          
          for (const typeName of feeAttributeTypes) {
            const attributes = getAttributesByTypeName(typeName);
            if (attributes && attributes.length > 0) {
              attributeData[typeName] = attributes;
            }
          }
        }
        
        setDynamicAttributes(attributeData);
      } catch (error) {
        console.error('Error loading dynamic attributes:', error);
      } finally {
        setIsLoadingAttributes(false);
      }
    };

    loadDynamicAttributes();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [merchantData]);

  // Handle dynamic attribute changes
  const handleAttributeChange = (attributeId, value) => {
    // Validate attributeId before proceeding
    if (!attributeId || attributeId === 'null' || attributeId === 'undefined') {
      console.error('❌ FeesStep: Invalid attributeId:', attributeId);
      return;
    }
    
    // Get current value to check if it changed
    const currentValueOrObject = attributeValues[attributeId];
    const currentValue = typeof currentValueOrObject === 'object' ? currentValueOrObject.value : currentValueOrObject;
    
    // Only update if the value actually changed
    if (currentValue === value) {
      return;
    }
    
    // Preserve UUID structure in edit mode
    const newValue = typeof currentValueOrObject === 'object' && currentValueOrObject.uuid
      ? { value: value, uuid: currentValueOrObject.uuid }  // Keep UUID structure in edit mode
      : value;  // Use simple string value in create mode
    
    const newAttributeValues = {
      ...attributeValues,
      [attributeId]: newValue
    };
    
    setAttributeValues(newAttributeValues);
    
    // Update parent formData
    if (updateFormData) {
      updateFormData({
        dynamic_attributes: newAttributeValues
      });
    }
    
  
  };

  // Handle dynamic attribute enabled changes
  const handleAttributeEnabledChange = (attributeId, enabled) => {
    // Validate attributeId before proceeding
    if (!attributeId || attributeId === 'null' || attributeId === 'undefined') {
      console.error('❌ FeesStep: Invalid attributeId for enabled change:', attributeId);
      return;
    }
    
    // Only update if the enabled state actually changed
    if (attributeEnabledValues[attributeId] === enabled) {
      return;
    }
    
    const newEnabledValues = {
      ...attributeEnabledValues,
      [attributeId]: enabled
    };
    
    setAttributeEnabledValues(newEnabledValues);
    
 
  };


  // Update parent formData when fee category changes
  useEffect(() => {
    if (updateFormData) {
      updateFormData({
        fees: {
          fee_category: feeCategory,
          use_fee_template: useFeeTemplate
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [feeCategory, useFeeTemplate]); // Removed updateFormData from dependencies

  // Sync local state with formData prop when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized.current) {
      setFeeCategory(formData.fees?.fee_category || 'Pass Through');
      setUseFeeTemplate(formData.fees?.use_fee_template || false);
      
      // Initialize attribute values and enabled states from formData
      if (formData.dynamic_attributes) {
        // Only update if the values are actually different to prevent infinite loops
        const currentValues = JSON.stringify(attributeValues);
        const newValues = JSON.stringify(formData.dynamic_attributes);
        
        if (currentValues !== newValues) {
          setAttributeValues(formData.dynamic_attributes);
          
          // Auto-enable fields that have values
          const enabledValues = {};
          Object.entries(formData.dynamic_attributes).forEach(([attributeId, valueOrObject]) => {
            // Handle both old format (string value) and new format (object with value and uuid)
            const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
            // Enable if value exists and is not empty/default
            enabledValues[attributeId] = value && value !== '0.0000' && value !== '0' && value !== '';
          });
          setAttributeEnabledValues(enabledValues);
          
       
        }
      }
      
      isInitialized.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [formData]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Fee Category Template */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            Use Fee category template
          </Typography>
          <Switch
            checked={useFeeTemplate}
            onChange={(e) => setUseFeeTemplate(e.target.checked)}
          />
        </Box>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={feeCategory}
            onChange={(e) => setFeeCategory(e.target.value)}
            sx={{ gap: 3 }}
          >
            <FormControlLabel value="Tier" name='merchant[fee_category]' control={<Radio />} label="Tier" />
            <FormControlLabel value="Pass Through" name='merchant[fee_category]' control={<Radio />} label="Pass Through" />
            <FormControlLabel value="Flat Rate" name='merchant[fee_category]' control={<Radio />} label="Flat Rate" />
            <FormControlLabel value="Other" name='merchant[fee_category]' control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Interchange Discount Section */}
      {feeCategory === 'Pass Through' && (
        <FormSection
          title="Interchange Discount"
          bgColor="blue.50"
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 4 }}>
            {/* Visa/MasterCard, Discover - Dynamic */}
            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Visa/MasterCard, Discover</Typography>
              <DynamicAttributeRenderer
                attributes={dynamicAttributes['Visa/MasterCard, Discover'] || []}
                values={attributeValues}
                onChange={handleAttributeChange}
                isLoading={isLoadingAttributes}
              />
            </Box>

            {/* American Express - Dynamic */}
            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>American Express</Typography>
              <DynamicAttributeRenderer
                attributes={dynamicAttributes['American Express'] || []}
                values={attributeValues}
                onChange={handleAttributeChange}
                isLoading={isLoadingAttributes}
              />
            </Box>

            {/* Pin Debit - Dynamic */}
            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Pin Debit</Typography>
              <DynamicAttributeRenderer
                attributes={dynamicAttributes['Pin Debit'] || []}
                values={attributeValues}
                onChange={handleAttributeChange}
                isLoading={isLoadingAttributes}
              />
            </Box>
          </Box>
        </FormSection>
      )}

      {/* Flat Rate Section */}
      {feeCategory === 'Flat Rate' && (
        <FormSection
          title="Flat Rate"
          bgColor="blue.50"
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>Flat Rate</Typography>
              <CurrencyInput
                value={flatRate}
                onChange={setFlatRate}
                placeholder="0.0000"
                width={120}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>Flat Rate Amex</Typography>
              <CurrencyInput
                value={flatRateAmex}
                onChange={setFlatRateAmex}
                placeholder="0.0000"
                width={120}
              />
            </Box>
          </Box>
        </FormSection>
      )}

      {/* Authorization, Monthly AND Miscellaneous Fees */}
      <FormSection
        title="Authorization, Monthly AND Miscellaneous Fees"
        bgColor="grey.50"
      >
        {/* Authorization & Per Item Fees - Dynamic */}
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Authorization & Per Item Fees</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>

          <DynamicFeeRenderer
            attributes={dynamicAttributes['Authorization & Per Item Fees'] || []}
            values={attributeValues}
            onChange={handleAttributeChange}
            enabledValues={attributeEnabledValues}
            onEnabledChange={handleAttributeEnabledChange}
            isLoading={isLoadingAttributes}
          />

        </Box>

        {/* Monthly Fees - Dynamic */}
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Monthly Fees</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>
          <DynamicFeeRenderer
            attributes={dynamicAttributes['Monthly Fees'] || []}
            values={attributeValues}
            onChange={handleAttributeChange}
            enabledValues={attributeEnabledValues}
            onEnabledChange={handleAttributeEnabledChange}
            isLoading={isLoadingAttributes}
          />
        </Box>

        {/* Miscellaneous Fees - Dynamic */}
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Miscellaneous Fees</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 4 }}>
          <DynamicFeeRenderer
            attributes={dynamicAttributes['Miscellaneous Fees'] || []}
            values={attributeValues}
            onChange={handleAttributeChange}
            enabledValues={attributeEnabledValues}
            onEnabledChange={handleAttributeEnabledChange}
            isLoading={isLoadingAttributes}
          />
          {/* Keep special fields that don't fit the standard pattern */}
          {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={highRiskRegistrationFeeEnabled}
                  onChange={(e) => setHighRiskRegistrationFeeEnabled(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">High Risk Registration Fee</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CurrencyInput
                  value={highRiskRegistrationFee}
                  onChange={setHighRiskRegistrationFee}
                  placeholder="0.0000"
                  width={120}
                  disabled={!highRiskRegistrationFeeEnabled}
                />
                <FormControl size="small">
                  <RadioGroup
                    row
                    value={highRiskType}
                    onChange={(e) => setHighRiskType(e.target.value)}
                  >
                    <FormControlLabel value="Merchant" control={<Radio size="small" />} label="Merchant" />
                    <FormControlLabel value="Agent" control={<Radio size="small" />} label="Agent" />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">Agreement Expiration Date</Typography>
              <FormSelect
                value={agreementExpiration}
                onChange={(e) => setAgreementExpiration(e.target.value)}
                options={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' }
                ]}
                sx={{ width: 80 }}
              />
            </Box>
          </Box> */}
        </Box>

      </FormSection>

      {/* Gateway Fees - Dynamic */}
      <FormSection
        title="Gateway Fees"
        bgColor="grey.50"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'grey.600' }}>Retail, Non-Retail, Wireless</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          <DynamicFeeRenderer
            attributes={dynamicAttributes['Retail, Non-Retail, Wireless'] || []}
            values={attributeValues}
            onChange={handleAttributeChange}
            enabledValues={attributeEnabledValues}
            onEnabledChange={handleAttributeEnabledChange}
            isLoading={isLoadingAttributes}
          />
        </Box>
      </FormSection>

      {/* Wireless Fees - Dynamic */}
      <FormSection
        title="Wireless Fees"
        bgColor="grey.50"
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          <DynamicFeeRenderer
            attributes={dynamicAttributes['Wireless Fees'] || []}
            values={attributeValues}
            onChange={handleAttributeChange}
            enabledValues={attributeEnabledValues}
            onEnabledChange={handleAttributeEnabledChange}
            isLoading={isLoadingAttributes}
          />
          {/* Keep special fields that don't fit the standard pattern */}
          {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">Quantity</Typography>
              <FormSelect
                value={wirelessQuantity}
                onChange={(e) => setWirelessQuantity(e.target.value)}
                options={[
                  { value: '0', label: '0' },
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' }
                ]}
                sx={{ width: 80 }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">Wireless Plan</Typography>
              <FormSelect
                value={wirelessPlan}
                onChange={(e) => setWirelessPlan(e.target.value)}
                options={[
                  { value: 'None', label: 'None' },
                  { value: 'Basic', label: 'Basic' },
                  { value: 'Standard', label: 'Standard' },
                  { value: 'Premium', label: 'Premium' }
                ]}
                sx={{ width: 120 }}
              />
            </Box>
          </Box> */}
        </Box>

      </FormSection>

    </Box>
  );
};

export default FeesStep;
